
import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import DOMPurify from 'dompurify';
import { Helmet } from 'react-helmet-async';
import { Link, useParams, Navigate } from 'react-router-dom';
import { useBooking } from '@/contexts/BookingContext';
import Header from '@/components/sections/Header';
import Footer from '@/components/sections/Footer';
import { blogDb, BLOG_ID } from '@/integrations/supabase/client';
import Breadcrumbs from '@/components/Breadcrumbs';
import drPhoto from '@/assets/dr-luis-brunello-cirurgiao-plastico.webp';
import drAvatar from '@/assets/dr-luis-brunello-avatar.webp';
import { normalizeBlogHtml } from '@/utils/normalizeBlogHtml';

const ProceduresSection = lazy(() => import('@/components/sections/ProceduresSection'));
const JourneySection = lazy(() => import('@/components/sections/JourneySection'));
const TestimonialsSection = lazy(() => import('@/components/sections/TestimonialsSection'));
const StructureSection = lazy(() => import('@/components/sections/StructureSection'));
const VideoSection = lazy(() => import('@/components/sections/VideoSection'));
const AboutSection = lazy(() => import('@/components/sections/AboutSection'));
const BlogSection = lazy(() => import('@/components/sections/BlogSection'));
const LocationSection = lazy(() => import('@/components/sections/LocationSection'));

interface PostData {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  date: string;
  reading_time: string;
  image: string | null;
  content: string;
  /** `date` é texto de exibição ("03 abr 2026"). Datas de schema/OG usam estes: */
  created_at?: string | null;
  updated_at?: string | null;
  meta_title?: string | null;
  meta_description?: string | null;
}

const ARTICLE_REFERENCES: Record<string, Array<{ title: string; url: string }>> = {
  'abdominoplastia-quem-realmente-precisa-da-cirurgia': [
    { title: 'American Society of Plastic Surgeons — Abdominoplastia', url: 'https://www.plasticsurgery.org/cosmetic-procedures/tummy-tuck' },
  ],
  'lipoaspiracao-o-que-realmente-muda-no-contorno-corporal': [
    { title: 'Sociedade Brasileira de Cirurgia Plástica — Lipoaspiração', url: 'https://www.cirurgiaplastica.org.br/cirurgias-e-procedimentos/lipoaspiracao/' },
  ],
  'blefaroplastia-o-que-saber-antes': [
    { title: 'American Society of Plastic Surgeons — Blefaroplastia', url: 'https://www.plasticsurgery.org/cosmetic-procedures/eyelid-surgery' },
  ],
  'abdominoplastia-o-que-saber-antes-da-cirurgia': [
    { title: 'American Society of Plastic Surgeons — Abdominoplastia', url: 'https://www.plasticsurgery.org/cosmetic-procedures/tummy-tuck' },
  ],
  'mastopexia-com-protese-o-que-saber-antes': [
    { title: 'American Society of Plastic Surgeons — Mastopexia', url: 'https://www.plasticsurgery.org/cosmetic-procedures/breast-lift' },
    { title: 'FDA — O que considerar antes de colocar implantes mamários', url: 'https://www.fda.gov/medical-devices/breast-implants/things-consider-getting-breast-implants' },
  ],
  'mama-flacida-como-corrigir-e-quando-a-mastopexia-e-indicada': [
    { title: 'American Society of Plastic Surgeons — Mastopexia', url: 'https://www.plasticsurgery.org/cosmetic-procedures/breast-lift' },
  ],
  'protese-de-silicone-qual-escolher-fatores-que-influenciam': [
    { title: 'FDA — Tipos de implantes mamários', url: 'https://www.fda.gov/medical-devices/breast-implants/types-breast-implants' },
  ],
  'mamoplastia-de-aumento-antes-e-depois-como-alcancar-um-resultado-natural-e-proporcional': [
    { title: 'FDA — O que considerar antes de colocar implantes mamários', url: 'https://www.fda.gov/medical-devices/breast-implants/things-consider-getting-breast-implants' },
  ],
  'mastopexia-sem-protese-para-quem-e-indicada-e-o-que-esperar-da-cirurgia': [
    { title: 'American Society of Plastic Surgeons — Mastopexia', url: 'https://www.plasticsurgery.org/cosmetic-procedures/breast-lift' },
  ],
};

const fitSeoText = (text: string, max: number) =>
  text.length <= max ? text : `${text.slice(0, max - 1).replace(/\s+\S*$/, '')}…`;

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { handleWhatsAppOpen } = useBooking();
  const [post, setPost] = useState<PostData | null>(null);
  const [recentPosts, setRecentPosts] = useState<PostData[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [pastMiniCard, setPastMiniCard] = useState(false);
  const [reachedAuthor, setReachedAuthor] = useState(false);
  const [fixedRight, setFixedRight] = useState(24);
  const asideRef = useRef<HTMLElement>(null);
  const miniCardRef = useRef<HTMLDivElement>(null);
  const authorCardRef = useRef<HTMLDivElement>(null);
  const showFixed = pastMiniCard && !reachedAuthor;

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      const { data, error } = await blogDb
        .from('posts')
        .select('slug, title, excerpt, category, date, reading_time, image, content, created_at, updated_at, meta_title, meta_description')
        .eq('blog_id', BLOG_ID)
        .eq('slug', slug!)
        .eq('status', 'published')
        .single();

      if (error || !data) {
        setNotFound(true);
      } else {
        setPost(data);
      }

      const { data: recent } = await blogDb
        .from('posts')
        .select('slug, title, excerpt, category, date, reading_time, image, content')
        .eq('blog_id', BLOG_ID)
        .eq('status', 'published')
        .neq('slug', slug!)
        .order('created_at', { ascending: false })
        .limit(3);

      if (recent) setRecentPosts(recent);
      setLoading(false);
    };
    fetchPost();
  }, [slug]);

  // Calcula a posição horizontal do fixed card para alinhar com o sidebar
  useEffect(() => {
    const update = () => {
      if (!asideRef.current) return;
      const rect = asideRef.current.getBoundingClientRect();
      setFixedRight(Math.max(16, window.innerWidth - rect.right));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [loading]);

  // Ativa quando o mini card original saiu pelo TOPO (user passou por ele)
  useEffect(() => {
    const card = miniCardRef.current;
    if (!card) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setPastMiniCard(false);
        } else {
          setPastMiniCard(entry.boundingClientRect.top < 0);
        }
      },
      { threshold: 0 }
    );
    observer.observe(card);
    return () => observer.disconnect();
  }, [loading]);

  // Desativa quando o topo do card do autor chega à posição do card fixo (112px)
  useEffect(() => {
    const handleScroll = () => {
      const card = authorCardRef.current;
      if (!card) return;
      setReachedAuthor(card.getBoundingClientRect().top <= 112);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loading]);

  if (notFound) return <Navigate to="/blog" replace />;

  if (loading || !post) {
    return (
      <div className="min-h-screen bg-dark-base flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const seoTitle = fitSeoText(post.meta_title || post.title, 65);
  const seoDescription = fitSeoText(post.meta_description || post.excerpt, 155);
  const articleReferences = ARTICLE_REFERENCES[post.slug] || [];
  const updatedDate = (post.updated_at || post.created_at)?.slice(0, 10).split('-').reverse().join('/');

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={`https://luisbrunello.com.br/blog/${post.slug}`} />
        <meta property="og:title" content={post.meta_title || post.title} />
        <meta property="og:description" content={post.meta_description || post.excerpt} />
        <meta property="og:url" content={`https://luisbrunello.com.br/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Dr. Luis Brunello" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:image" content={post.image || 'https://luisbrunello.com.br/og-image.jpg'} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.meta_title || post.title} />
        <meta name="twitter:description" content={post.meta_description || post.excerpt} />
        <meta name="twitter:image" content={post.image || 'https://luisbrunello.com.br/og-image.jpg'} />
        <meta property="article:published_time" content={post.created_at || ''} />
        <meta property="article:modified_time" content={post.updated_at || post.created_at || ''} />
        <meta property="article:author" content="Dr. Luis Brunello" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          '@id': `https://luisbrunello.com.br/blog/${post.slug}#article`,
          mainEntityOfPage: `https://luisbrunello.com.br/blog/${post.slug}`,
          headline: (post.meta_title || post.title).slice(0, 110),
          description: post.meta_description || post.excerpt,
          image: post.image || 'https://luisbrunello.com.br/og-image.jpg',
          articleSection: post.category,
          datePublished: post.created_at,
          dateModified: post.updated_at || post.created_at,
          inLanguage: 'pt-BR',
          ...(articleReferences.length ? { citation: articleReferences.map(reference => reference.url) } : {}),
          author: { '@id': 'https://luisbrunello.com.br/#physician' },
          publisher: { '@id': 'https://luisbrunello.com.br/#physician' },
        })}</script>
      </Helmet>

      <Header
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        scrollToSection={() => {}}
        onOpenBooking={() => handleWhatsAppOpen()}
      />

      {/* Barra fixa mobile — visível apenas em telas < lg enquanto lê o post */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-dark-surface/95 backdrop-blur-sm border-t border-white/10 px-4 py-3 flex items-center gap-3">
        <img src={drAvatar} alt="Dr. Luis Brunello — Cirurgião Plástico em Curitiba" title="Dr. Luis Brunello — CRM-PR 40.350" className="w-10 h-10 rounded-full object-cover object-top shrink-0 border border-white/10" width="40" height="40" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-light-primary leading-tight">Dr. Luis Brunello</p>
          <p className="text-[10px] text-light-muted/50 leading-tight">Cirurgião Plástico · Curitiba</p>
        </div>
        <button
          onClick={() => handleWhatsAppOpen()}
          className="shrink-0 bg-dark-base hover:bg-dark-elevated border border-white/10 hover:border-gold/30 text-light-primary text-xs font-semibold px-4 py-2.5 rounded-xl transition-all duration-200 whitespace-nowrap"
        >
          Agendar
        </button>
      </div>

      <main className="bg-dark-base pb-20 lg:pb-0" style={{ paddingTop: '96px' }}>
        <div className="container mx-auto px-6 lg:px-12 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-12 items-start max-w-6xl mx-auto">

            {/* ── Article ── */}
            <article>
              <header className="mb-10">
                <Breadcrumbs
                  items={[
                    { label: 'Home', href: '/' },
                    { label: 'Blog', href: '/blog' },
                    { label: post.title },
                  ]}
                />

                <div className="mb-5">
                  <span className="inline-block text-[10px] uppercase tracking-widest bg-gold/10 text-gold border border-gold/25 rounded-full px-4 py-1.5 font-medium">
                    {post.category}
                  </span>
                </div>

                <h1 className="text-3xl md:text-4xl lg:text-5xl text-light-primary font-display font-normal leading-tight tracking-tight mb-7">
                  {post.title}
                </h1>

                <div className="flex flex-wrap items-center gap-6 text-light-muted/60 text-sm">
                  <div className="flex items-center gap-2">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/></svg>
                    <span>{post.date}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    <span>{post.reading_time}</span>
                  </div>
                </div>
              </header>

              <div className="w-full aspect-video rounded-2xl overflow-hidden bg-dark-surface border border-white/5 mb-10">
                {post.image ? (
                  <img src={post.image} alt={post.title} title={post.title} className="w-full h-full object-cover" loading="eager" {...{ fetchpriority: 'high' }} decoding="async" width="960" height="540" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                    <div className="w-8 h-8 rounded-full border border-gold/20 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-gold/40" />
                    </div>
                    <span className="text-light-muted/30 text-[9px] uppercase tracking-widest text-center px-2">Imagem em breve</span>
                  </div>
                )}
              </div>

              <div className="prose-blog text-light-muted leading-relaxed">
                <div dangerouslySetInnerHTML={{ __html: normalizeBlogHtml(DOMPurify.sanitize(post.content)) }} />
              </div>

              {articleReferences.length > 0 && (
                <section aria-labelledby="article-references" className="mt-10 pt-8 border-t border-white/5">
                  <h2 id="article-references" className="text-xl text-light-primary mb-4">Referências institucionais</h2>
                  <ul className="space-y-2 text-sm text-light-muted/70">
                    {articleReferences.map(reference => (
                      <li key={reference.url}>
                        <a href={reference.url} target="_blank" rel="noopener noreferrer" className="text-gold/80 hover:text-gold underline underline-offset-4">
                          {reference.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <div ref={authorCardRef} className="mt-12 pt-8 border-t border-white/5 flex items-center gap-5 p-6 bg-dark-surface rounded-2xl">
                <img src={drAvatar} alt="Dr. Luis Brunello — Cirurgião Plástico em Curitiba" title="Dr. Luis Brunello — CRM-PR 40.350" className="w-14 h-14 rounded-full object-cover object-top shrink-0 border border-white/10" loading="lazy" width="56" height="56" />
                <div>
                  <p className="font-semibold text-light-primary text-sm">Dr. Luis Brunello</p>
                  <p className="text-light-muted/60 text-xs mt-0.5">Cirurgião Plástico · CRM-PR 40.350 | RQE 38.853 · Membro da SBCP</p>
                  {updatedDate && <p className="text-light-muted/45 text-xs mt-1">Última atualização: {updatedDate}</p>}
                </div>
              </div>
            </article>

            {/* ── Sidebar ── */}
            <aside ref={asideRef} className="lg:sticky lg:top-28 space-y-6">
              <div className="rounded-2xl overflow-hidden border border-white/8 bg-dark-surface">
                <div className="px-5 pt-5 pb-3">
                  <p className="text-base font-semibold text-light-primary leading-snug">Entre em contato</p>
                  <p className="text-xs text-light-muted/60 mt-0.5">Reserve um horário</p>
                </div>
                <div className="px-5">
                  <img src={drPhoto} alt="Dr. Luis Brunello — Cirurgião Plástico em Curitiba" title="Dr. Luis Brunello — CRM-PR 40.350" className="w-full rounded-xl object-cover object-top" style={{ aspectRatio: '4/5' }} loading="lazy" width="280" height="350" />
                </div>
                <div className="px-5 py-5 text-center">
                  <p className="text-sm text-light-muted/70 leading-relaxed mb-5">
                    Procedimentos planejados individualmente, com técnica refinada e acompanhamento próximo pelo Dr. Luis Brunello.
                  </p>
                  <button onClick={() => handleWhatsAppOpen()} className="w-full bg-dark-base hover:bg-dark-elevated border border-white/10 hover:border-gold/30 text-light-primary text-sm font-semibold py-3 rounded-xl transition-all duration-200">
                    Agendar consulta
                  </button>
                </div>
              </div>

              <div className="bg-dark-surface border border-white/5 rounded-2xl p-6">
                <h2 className="text-base font-semibold text-light-primary mb-4">Explore por temas</h2>
                <div className="relative mb-5">
                  <input
                    name="q"
                    aria-label="Buscar matérias"
                    type="text"
                    placeholder="Busque aqui..."
                    className="w-full h-10 pl-4 pr-10 rounded-xl bg-dark-base border border-white/8 text-light-primary text-sm placeholder:text-light-muted/40 focus:outline-none focus:border-gold/30 transition-colors"
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        const val = (e.target as HTMLInputElement).value.trim();
                        if (val) window.location.href = `/blog?q=${encodeURIComponent(val)}`;
                      }
                    }}
                  />
                  <svg className="absolute right-3 top-1/2 -translate-y-1/2 text-light-muted/40" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                </div>
                <p className="text-xs font-semibold text-light-primary uppercase tracking-widest mb-3">Tópicos mais lidos</p>
                <ul className="flex flex-wrap gap-2 list-none p-0 m-0">
                  {['Cirurgias Mamárias', 'Contorno Corporal', 'Pós-Operatório', 'Procedimentos Faciais', 'Nutrição e Cirurgia'].map(tag => (
                    <li key={tag} className="list-none">
                      <Link to={`/blog?cat=${encodeURIComponent(tag)}`} className="text-xs text-light-muted border border-white/10 rounded-full px-3 py-1.5 hover:border-gold/30 hover:text-gold transition-all inline-block">
                        {tag}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-dark-surface border border-white/5 rounded-2xl p-6">
                <h2 className="text-xs font-semibold text-light-primary uppercase tracking-widest mb-5">Outros artigos</h2>
                <ul className="space-y-5">
                  {recentPosts.map(p => (
                    <li key={p.slug}>
                      <Link to={`/blog/${p.slug}`} className="group flex gap-3 items-start">
                        <div className="w-14 h-14 rounded-lg bg-dark-base border border-white/5 shrink-0 overflow-hidden flex items-center justify-center">
                          {p.image ? (
                            <img src={p.image} alt={p.title} title={p.title} className="w-full h-full object-cover" loading="lazy" decoding="async" width="56" height="56" />
                          ) : (
                            <div className="w-2 h-2 rounded-full bg-gold/30" />
                          )}
                        </div>
                        <div className="flex flex-col gap-1 min-w-0">
                          <span className="text-[10px] text-gold/60 uppercase tracking-wide">{p.category}</span>
                          <span className="text-sm text-light-muted group-hover:text-gold transition-colors line-clamp-2 leading-snug">
                            {p.title}
                          </span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link to="/blog" className="mt-5 block text-center text-xs text-gold/60 hover:text-gold transition-colors">
                  Ver todos os artigos →
                </Link>
              </div>

              {/* Mini card — referência para o IntersectionObserver */}
              <div ref={miniCardRef} className="rounded-2xl border border-white/8 bg-dark-surface p-4 flex items-center gap-3">
                <img
                  src={drPhoto}
                  alt="Dr. Luis Brunello"
                  className="w-12 h-12 rounded-full object-cover object-top shrink-0 border border-white/10"
                  width="48" height="48"
                  loading="lazy"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-light-primary leading-tight">Dr. Luis Brunello</p>
                  <p className="text-[10px] text-light-muted/50 leading-tight mt-0.5">Cirurgião Plástico · Curitiba</p>
                </div>
                <button
                  onClick={() => handleWhatsAppOpen()}
                  className="shrink-0 bg-dark-base hover:bg-dark-elevated border border-white/10 hover:border-gold/30 text-light-primary text-xs font-semibold px-3 py-2 rounded-xl transition-all duration-200 whitespace-nowrap"
                >
                  Agendar
                </button>
              </div>
            </aside>
          </div>
        </div>

        {/* Clone fixed — aparece quando o mini card do sidebar sair do viewport (desktop only) */}
        <div
          className="hidden lg:block fixed top-28 z-40 w-[320px] transition-all duration-300"
          style={{
            right: fixedRight,
            opacity: showFixed ? 1 : 0,
            transform: showFixed ? 'translateY(0)' : 'translateY(-12px)',
            pointerEvents: showFixed ? 'auto' : 'none',
          }}
        >
          <div className="rounded-2xl border border-white/8 bg-dark-surface/95 backdrop-blur-sm p-4 flex items-center gap-3 shadow-xl shadow-black/40">
            <img src={drPhoto} alt="Dr. Luis Brunello — Cirurgião Plástico em Curitiba" title="Dr. Luis Brunello — CRM-PR 40.350" className="w-12 h-12 rounded-full object-cover object-top shrink-0 border border-white/10" width="48" height="48" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-light-primary leading-tight">Dr. Luis Brunello</p>
              <p className="text-[10px] text-light-muted/50 leading-tight mt-0.5">Cirurgião Plástico · Curitiba</p>
            </div>
            <button
              onClick={() => handleWhatsAppOpen()}
              className="shrink-0 bg-dark-base hover:bg-dark-elevated border border-white/10 hover:border-gold/30 text-light-primary text-xs font-semibold px-3 py-2 rounded-xl transition-all duration-200 whitespace-nowrap"
            >
              Agendar
            </button>
          </div>
        </div>

        <div className="w-full h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent" />

        <Suspense fallback={null}><ProceduresSection onOpenBooking={() => handleWhatsAppOpen()} /></Suspense>
        <Suspense fallback={null}><JourneySection /></Suspense>
        <Suspense fallback={null}><TestimonialsSection /></Suspense>
        <Suspense fallback={null}><StructureSection /></Suspense>
        <Suspense fallback={null}><VideoSection /></Suspense>
        <Suspense fallback={null}><AboutSection onOpenBooking={() => handleWhatsAppOpen()} /></Suspense>
        <Suspense fallback={null}><BlogSection /></Suspense>
        <Suspense fallback={null}><LocationSection onOpenBooking={() => handleWhatsAppOpen()} /></Suspense>
      </main>

      <Footer />
    </>
  );
};

export default BlogPostPage;
